import { yupResolver } from '@hookform/resolvers/yup'
import { FC, useEffect } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import TextInput from '@/components/text-input'
import { Button, Card } from '@/components/ui'
import translate from '@/core/services/translate'
import ShowCaptcha from '../../../core/styles/showCaptcha'
import { getTranslation, requestForgotPasswordCode } from '../../services/auth'

export interface PasswordFormValues {
  password: string
}

interface LoginPasswordProps {
  submitPassword: (password: string) => void
  submitError?: string
  isLoggingIn?: boolean
  phone: string
}

export const LoginPasswordForm: FC<LoginPasswordProps> = ({
  submitPassword,
  submitError,
  isLoggingIn,
  phone,
}) => {
  const schema = yup.object().shape({
    password: yup.string().required('Password is required'),
  })

  const navigation = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    setError('root', {
      message: submitError,
    })
  }, [submitError])

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    setError,
    setFocus,
  } = useForm<PasswordFormValues>({
    mode: 'onChange',
    resolver: yupResolver<PasswordFormValues>(schema),
  })

  useEffect(() => {
    setFocus('password')
  }, [setFocus])

  const submit = (data: PasswordFormValues) => {
    submitPassword(data.password)
  }

  const getCaptchaToken = async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return ''
    }

    return await executeRecaptcha('requestCode')
  }

  const language = getTranslation()

  return (
    <div className="w-full">
      <ShowCaptcha />
      <form onSubmit={handleSubmit(submit)} className="w-full">
        <TextInput
          type="password"
          label={translate(language.value, 'common.password')}
          placeholder={translate(language.value, 'common.enterPassword')}
          {...register('password')}
          error={errors.password?.message}
        />

        {errors.root?.message && (
          <>
            <Card className="p-4 border-accent-3 flex items-center gap-2 mt-4">
              <Icon
                icon="warning"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                className="text-accent-3 shrink-0"
                fill="none"
              />
              <p
                className="text-danger mb-0"
                style={{
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '21px',
                }}
              >
                Password is not correct, you can reset your Password by sending
                a message to your phone
              </p>
            </Card>
            <Button
              onClick={async () => {
                await requestForgotPasswordCode(phone, await getCaptchaToken())
                sessionStorage.setItem('reset_password_phone', phone)
                navigation(`/account/reset-password`, {
                  state: { phone: phone },
                })
              }}
              variant="primary-outline"
              className="w-full mt-3"
              type="button"
            >
              {translate(language.value, 'buttons.resetPassword')}
            </Button>
          </>
        )}
        <Button
          disabled={isLoggingIn || !isValid}
          loading={isLoggingIn}
          className="mt-3 w-full"
          type="submit"
          variant="primary"
        >
          {translate(language.value, 'buttons.continue')}
        </Button>

        <p
          className="text-body-medium text-center text-monochrome-2 mt-4"
          dangerouslySetInnerHTML={{
            __html: translate(language.value, 'privacyText'),
          }}
        />
      </form>
      <div style={{ justifyContent: 'center', marginTop: 20 }}>
        <div id="recaptcha-custom"></div>
      </div>
    </div>
  )
}
